import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type SystemUser, availableRoles } from '@/mocks/accessControl';

interface UserFiltersProps {
  search: string;
  onSearchChange: (val: string) => void;
  roleFilter: string;
  onRoleFilterChange: (val: string) => void;
  statusFilter: string;
  onStatusFilterChange: (val: string) => void;
  onCreateUser: () => void;
}

export default function UserFilters({
  search,
  onSearchChange,
  roleFilter,
  onRoleFilterChange,
  statusFilter,
  onStatusFilterChange,
  onCreateUser,
}: UserFiltersProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-1 min-w-0">
        {/* Search */}
        <div className="relative w-full sm:w-64">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('accessControl.searchUsers')}
            className="xl-input pl-9 text-sm"
          />
        </div>

        {/* Role Filter */}
        <div className="relative w-full sm:w-44">
          <select
            value={roleFilter}
            onChange={(e) => onRoleFilterChange(e.target.value)}
            className="xl-input cursor-pointer text-sm appearance-none pr-8"
          >
            <option value="">{t('accessControl.allRoles')}</option>
            {availableRoles.map((role) => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none" />
        </div>

        {/* Status Filter */}
        <div className="relative w-full sm:w-36">
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            className="xl-input cursor-pointer text-sm appearance-none pr-8"
          >
            <option value="">{t('accessControl.allStatuses')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="pending">{t('common.pending')}</option>
            <option value="inactive">{t('common.inactive')}</option>
          </select>
          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none" />
        </div>
      </div>

      {/* Create User Button */}
      <button
        onClick={onCreateUser}
        className="inline-flex items-center gap-2 h-10 px-4 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-user-add-line" />
        {t('accessControl.createUser')}
      </button>
    </div>
  );
}